import React, { ChangeEvent } from "react"
import { TextField, MenuItem, IconButton, Tooltip } from "@mui/material"
import Icon from "@mdi/react"
import {
	mdiTrashCanOutline,
	mdiContentCopy,
	mdiSquareRoundedOutline,
	mdiCheckboxMarked,
	mdiPencil,
	mdiChevronUp,
	mdiChevronDown,
	mdiInformation,
} from "@mdi/js"

import TextInput from "components/Forms/TextInput"
import LockingSystemRowConfigurations from "./LockingSystemRowConfigurations/LockingSystemRowConfigurations"
import StyledLockingSystemCell from "./StyledLockingSystemCell"
import LockingSystemItemWrapper from "containers/LockingSystem/Matrix/LockingSystemItemWrapper"

import formatPrice from "@secureo/common/utils/forex/formatPrice"

import { LockingSystemReducerState, MoveDirection } from "reducers/lockingSystemReducer"
import {
	ComponentCharges,
	LockingSystemComponent,
	LockingSystemPreviewProduct,
	LockingSystemType,
} from "@secureo/common/typings/LockingSystem"

import CheckAll from "svg/lockingSystem/check-all-horizontal.svg"

export const defaultLockName = "Tür"

const LockingSystemRow = ({
	isLoading,
	isFrozen,
	lockingSystemType,
	rowIndex,
	lockingComponent,
	charges,
	matrix,
	availableLockingComponents,
	setCellValue,
	removeLock,
	duplicateLock,
	setComponentName,
	setComponentLockingSystemProduct,
	setComponentOuterLength,
	setComponentInnerLength,
	setEditComponentModalUuid,
	moveMatrixComponent,
	toggleLockingComponentSelections,
}: Props) => {
	const {
		uuid,
		name,
		selectedSystem,
		selectedOuterLength,
		selectedInnerLength,
		masterVariantSku,
		attributes,
		totalGrossPriceCents,
	} = lockingComponent

	const matrixRow = matrix?.[rowIndex] ?? []
	const gridRowName = `lockingComponent ${rowIndex + 1}`

	const toggleCell = (cellIndex: number, isSelected: boolean) => {
		setCellValue(rowIndex, cellIndex, !isSelected)
	}

	const remove = () => {
		removeLock(uuid)
	}

	const duplicate = () => {
		duplicateLock(uuid)
	}

	const onComponentNameChange = (event: any) => {
		const name = event.target.value

		setComponentName(uuid, name)
	}

	const onLockingSystemProductChange = (
		event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) => {
		const sku = event.target.value
		const lockingSystemPreviewProduct = availableLockingComponents.find(
			(previewProduct) => previewProduct.sku === sku,
		)

		setComponentLockingSystemProduct(uuid, lockingSystemPreviewProduct)
	}

	const onToggleSelections = () => {
		toggleLockingComponentSelections(uuid)
	}

	const fallbackName = `${defaultLockName} ${rowIndex + 1}`

	return (
		<>
			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="edit">
				<LockingSystemItemWrapper isRowFirstItem>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							marginTop: 5,
						}}
					>
						<button
							disabled={isFrozen ? false : isLoading}
							onClick={() => setEditComponentModalUuid(uuid)}
							id="edit-locking-system-row"
							style={{
								background: "rgba(70, 145, 206, 0.25)",
								width: 50,
								height: 40,
								borderRadius: 3,
								textAlign: "center",
								border: "none",
								outline: "none",
								cursor: "pointer",
							}}
						>
							<Icon
								path={isFrozen ? mdiInformation : mdiPencil}
								size={1}
								style={{ padding: 0 }}
								color="#4691CE"
							/>
						</button>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>

			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="position">
				<div style={{ position: "relative" }}>
					<LockingSystemItemWrapper>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginTop: 3,
								paddingLeft: 10,
							}}
						>
							<span>{rowIndex + 1}</span>
							<div>
								<IconButton
									disabled={isLoading}
									onClick={() => moveMatrixComponent(uuid, "down")}
									style={{ padding: 0 }}
									size="large"
								>
									<Icon path={mdiChevronUp} size={0.75} />
								</IconButton>
								<IconButton
									disabled={isLoading}
									onClick={() => moveMatrixComponent(uuid, "up")}
									style={{ padding: 0 }}
									size="large"
								>
									<Icon path={mdiChevronDown} size={0.75} />
								</IconButton>
							</div>
						</div>
					</LockingSystemItemWrapper>
				</div>
			</StyledLockingSystemCell>

			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="actions">
				<LockingSystemItemWrapper>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							marginTop: 10,
						}}
					>
						<IconButton
							disabled={isLoading || !removeLock}
							onClick={remove}
							style={{ padding: 5 }}
							size="large"
						>
							<Icon path={mdiTrashCanOutline} size={0.9} />
						</IconButton>
						<IconButton
							disabled={isLoading || !duplicateLock}
							onClick={duplicate}
							style={{ padding: 5 }}
							size="large"
						>
							<Icon path={mdiContentCopy} size={0.9} />
						</IconButton>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>

			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="doorName">
				<LockingSystemItemWrapper>
					<TextInput
						name="door-name"
						label={fallbackName}
						value={name}
						onChange={onComponentNameChange}
						placeholder={fallbackName}
						disabled={isLoading || !setComponentName}
						style={{ marginTop: 0 }}
						fullWidth
					/>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>

			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="lockingComponent">
				<LockingSystemItemWrapper>
					<TextField
						label={`Zylinder (${formatPrice(
							totalGrossPriceCents + (charges?.totalGrossChargeCents ?? 0),
						)})`}
						value={masterVariantSku}
						onChange={onLockingSystemProductChange}
						margin="normal"
						variant="filled"
						select
						fullWidth
						disabled={isLoading || !onLockingSystemProductChange}
						style={{ marginTop: 0 }}
					>
						{availableLockingComponents.map((lockingComponent) => {
							const { sku, name } = lockingComponent

							return (
								<MenuItem key={sku} value={sku}>
									{name}
								</MenuItem>
							)
						})}
					</TextField>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>

			<LockingSystemRowConfigurations
				isLoading={isLoading}
				uuid={uuid}
				gridRowName={gridRowName}
				attributes={attributes}
				selectedSystem={selectedSystem}
				selectedOuterLength={selectedOuterLength}
				selectedInnerLength={selectedInnerLength}
				setSelectedOuterLength={setComponentOuterLength}
				setSelectedInnerLength={setComponentInnerLength}
			/>

			{lockingSystemType === "mechanic" && (
				<>
					{matrixRow.map((isSelected, cellIndex) => {
						const id = `cell-${rowIndex + 1}-${cellIndex + 1}`
						const key = `${uuid}-${rowIndex}-${cellIndex}`

						const coordinates = `(${rowIndex + 1}, ${cellIndex + 1})`

						return (
							<StyledLockingSystemCell
								gridRowName={gridRowName}
								gridColumnName={`keyComponent ${cellIndex + 1}`}
								key={key}
							>
								<LockingSystemItemWrapper isCheckBoxItem={true} id={id}>
									<IconButton
										disabled={isLoading || !setCellValue}
										onClick={() => {
											toggleCell(cellIndex, isSelected)
										}}
										style={{
											height: 48,
											borderRadius: 5,
											marginTop: 3,
											marginLeft: 2,
										}}
										size="large"
									>
										<Icon
											path={
												isSelected
													? mdiCheckboxMarked
													: mdiSquareRoundedOutline
											}
											size={1}
											color={isSelected ? "#4691CE" : ""}
										/>
										<span
											style={{
												position: "absolute",
												top: 2,
												right: 2,
												fontSize: 8,
												color: "grey",
											}}
										>
											{coordinates}
										</span>
									</IconButton>
								</LockingSystemItemWrapper>
							</StyledLockingSystemCell>
						)
					})}
				</>
			)}
			<StyledLockingSystemCell gridRowName={gridRowName} gridColumnName="selectAllRow">
				<LockingSystemItemWrapper>
					<div style={{ textAlign: "center" }}>
						<Tooltip title="Ganze Zeile auswählen">
							<IconButton
								style={{ padding: 0, marginTop: 15, marginLeft: -3 }}
								disabled={isLoading || !toggleLockingComponentSelections}
								onClick={onToggleSelections}
								size="large"
							>
								<CheckAll />
							</IconButton>
						</Tooltip>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>
		</>
	)
}

interface Props {
	isLoading: boolean
	isFrozen: boolean
	lockingSystemType: LockingSystemType
	charges: ComponentCharges
	rowIndex: number
	lockingComponent: LockingSystemComponent
	matrix: LockingSystemReducerState["matrix"]
	availableLockingComponents?: LockingSystemPreviewProduct[]
	setCellValue?: (rowIndex: number, cellIndex: number, isSelected: boolean) => void
	removeLock?: (uuid: string) => void
	duplicateLock?: (uuid: string) => void
	setComponentName?: (uuid: string, name: string) => void
	setComponentQuantity?: (uuid: string, quantity: number) => void
	setComponentLockingSystemProduct?: (
		uuid: string,
		lockingSystemPreviewProduct: LockingSystemPreviewProduct,
	) => void
	setComponentOuterLength?: (uuid: string, selectedOuterLength: number) => void
	setComponentInnerLength?: (uuid: string, selectedOuterLength: number) => void
	setEditComponentModalUuid?: (uuid: string) => void
	moveMatrixComponent?: (uuid: string, direction: MoveDirection) => void
	toggleLockingComponentSelections?: (uuid: string) => void
}

export default LockingSystemRow
